import { resolveCinemaChain, INDEPENDENT_CHAIN_LABEL } from "./cinemaChainRecognition";

const FIELD_ALIASES = {
  theaterName: [
    "theater",
    "theater name",
    "cinema",
    "cinema name",
    "location name",
    "venue",
    "venue name",
  ],
  circuitName: [
    "circuit",
    "circuit name",
    "chain",
    "chain name",
    "exhibitor",
    "exhibitor name",
    "operator",
  ],
  address: [
    "address",
    "street",
    "street address",
    "address 1",
    "address line 1",
    "location address",
  ],
  city: ["city", "town"],
  // Source row numbers are presentation only. They are aliased so the column is
  // recognised and skipped — never treated as a theater field or an identity.
  // Source row numbers are presentation only ("#" normalizes to an empty
  // header). Aliasing the column means it is recognised and skipped rather
  // than read as a theater field — and it is never part of a theater's
  // identity, because upstream renumbers it whenever a row is inserted.
  sourceRowNumber: ["#", "no", "no.", "num", "number", "row", "index", "item"],
  state: ["state", "region", "province", "territory"],
  postalCode: ["zip", "zip code", "postal", "postal code", "postcode"],
  country: ["country", "nation"],
  latitude: ["lat", "latitude", "y"],
  longitude: ["lng", "lon", "long", "longitude", "x"],
  market: ["market", "market name", "dma", "dma name", "region market", "media market"],
  screenCount: ["screens", "screen count", "auditoriums", "auditorium count"],
  audienceMetric: [
    "attendance",
    "audience",
    "audience metric",
    "footfall",
    "visits",
    "impressions",
    "tickets",
  ],
};

const MAX_PREVIEW_ROWS = 8;

function normalizeHeader(value) {
  return String(value || "")
    .replace(/^\uFEFF/, "")
    .trim()
    .toLowerCase()
    .replace(/[_/-]+/g, " ")
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeValue(value) {
  return String(value ?? "").trim();
}

function normalizeNumber(value) {
  const normalized = normalizeValue(value).replace(/,/g, "");
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseCsvRows(text) {
  const rows = [];
  let row = [];
  let cell = "";
  let inQuotes = false;

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    const next = text[index + 1];

    if (char === "\"") {
      if (inQuotes && next === "\"") {
        cell += "\"";
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === "," && !inQuotes) {
      row.push(cell);
      cell = "";
      continue;
    }

    if ((char === "\n" || char === "\r") && !inQuotes) {
      if (char === "\r" && next === "\n") index += 1;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
      continue;
    }

    cell += char;
  }

  if (inQuotes) {
    throw new Error("Malformed CSV: unmatched quote found.");
  }

  row.push(cell);
  rows.push(row);
  return rows.filter((csvRow) => csvRow.some((value) => normalizeValue(value)));
}

// Presentation-oriented source files (title block, contact info, campaign
// resources) often carry the real table header several rows down, and summary
// rows after the table. Locate the header unambiguously: the first row that
// maps a theater column AND at least one location column. Returns 0 (legacy
// behavior) when the first row already looks like the header or nothing does.
const HEADER_SCAN_LIMIT = 40;

function locateCinemaTableHeaderIndex(csvRows) {
  const limit = Math.min(csvRows.length, HEADER_SCAN_LIMIT);
  for (let index = 0; index < limit; index += 1) {
    const candidate = csvRows[index].map((value) => normalizeValue(value));
    const map = buildFieldMap(candidate);
    const hasTheater = map.theaterName >= 0;
    const hasLocation =
      map.address >= 0 || map.city >= 0 || map.market >= 0 || map.latitude >= 0;
    if (hasTheater && hasLocation) return index;
  }
  return 0;
}

// The theater table ends at the first row carrying no location signal at all.
// Everything after it — "TOTAL: 75 theaters · 24 states" summary rows, ZIP-code
// recaps, copyright footers — is presentation content, not theater data.
//
// This applies whether or not the header was found mid-file: a sheet exported
// straight from the theater tab has its header on row 1 and its summary and ZIP
// recap rows immediately after the table, and those must not become theaters.
// Governed trailing-content signatures. A row carrying no location signal ends
// the theater table when its text is one of these — a totals line, the ZIP-code
// recap and its guidance sentence, or a copyright footer. The list is explicit
// on purpose: a row with no location fields is otherwise treated as a DATA row
// that is missing them (and reported as such), never silently truncated.
const CINEMA_TABLE_TRAILING_PATTERNS = [
  /^(?:grand\s+)?total\b/i,
  /^zip\s*codes?\b/i,
  /^recommend\b/i,
  /^(?:©|\(c\)|copyright\b)/i,
  /^summary\b/i,
];

function isCinemaTableTrailingRow(row) {
  const populated = row.map((value) => normalizeValue(value)).filter(Boolean);
  if (populated.length !== 1) return false;
  return CINEMA_TABLE_TRAILING_PATTERNS.some((pattern) => pattern.test(populated[0]));
}

function sliceCinemaTableRows(csvRows, headerIndex, headers, fieldMap) {
  const rows = csvRows.slice(headerIndex + 1);
  const terminal = rows.findIndex((row) => {
    const address = readField(row, fieldMap, "address");
    const city = readField(row, fieldMap, "city");
    const market = readField(row, fieldMap, "market");
    const latitude = readField(row, fieldMap, "latitude");
    if (address || city || market || latitude) return false;
    // A file whose header sat below an intro block is a presentation document:
    // everything past the table is prose, so the first location-less row ends
    // it. A file whose header is on row 1 ends only at governed trailing
    // content, so a genuine data row missing its location fields survives.
    return headerIndex > 0 || isCinemaTableTrailingRow(row);
  });
  return terminal >= 0 ? rows.slice(0, terminal) : rows;
}

function buildFieldMap(headers) {
  const normalizedHeaders = headers.map(normalizeHeader);
  return Object.fromEntries(
    Object.entries(FIELD_ALIASES).map(([field, aliases]) => {
      const normalizedAliases = aliases.map(normalizeHeader);
      const index = normalizedHeaders.findIndex((header) => normalizedAliases.includes(header));
      return [field, index >= 0 ? index : -1];
    })
  );
}

function readField(row, fieldMap, field) {
  const index = fieldMap[field];
  return index >= 0 ? normalizeValue(row[index]) : "";
}

function normalizeCinemaRow(row, headers, fieldMap) {
  const latitude = normalizeNumber(readField(row, fieldMap, "latitude"));
  const longitude = normalizeNumber(readField(row, fieldMap, "longitude"));
  const hasCoordinates = latitude != null && longitude != null;
  const hasPartialCoordinates = (latitude != null || longitude != null) && !hasCoordinates;
  const addressParts = [
    readField(row, fieldMap, "address"),
    readField(row, fieldMap, "city"),
    readField(row, fieldMap, "state"),
    readField(row, fieldMap, "postalCode"),
    readField(row, fieldMap, "country"),
  ].filter(Boolean);
  const hasAddress = addressParts.length >= 2 || Boolean(readField(row, fieldMap, "address"));
  const market = readField(row, fieldMap, "market");
  const rowReadiness = hasCoordinates
    ? "Geofence-ready"
    : hasAddress
    ? "Geocoding required"
    : market
    ? "Market-targeting-ready"
    : "Needs location fields";

  return {
    theaterName: readField(row, fieldMap, "theaterName"),
    circuitName: readField(row, fieldMap, "circuitName"),
    address: readField(row, fieldMap, "address"),
    city: readField(row, fieldMap, "city"),
    state: readField(row, fieldMap, "state"),
    postalCode: readField(row, fieldMap, "postalCode"),
    country: readField(row, fieldMap, "country"),
    latitude,
    longitude,
    market,
    screenCount: normalizeNumber(readField(row, fieldMap, "screenCount")),
    audienceMetric: normalizeNumber(readField(row, fieldMap, "audienceMetric")),
    rowReadiness,
    hasCoordinates,
    hasAddress,
    hasMarket: Boolean(market),
    hasPartialCoordinates,
    raw: Object.fromEntries(headers.map((header, index) => [header, normalizeValue(row[index])])),
  };
}

function uniqueCount(rows, selector) {
  const values = new Set();
  rows.forEach((row) => {
    const value = normalizeValue(selector(row));
    if (value) values.add(value.toLowerCase());
  });
  return values.size;
}

function buildMarketDistribution(rows) {
  const counts = new Map();
  rows.forEach((row) => {
    const key = normalizeValue(row.market || row.state || row.city || "Unknown Market");
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  return Array.from(counts.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
    .slice(0, 5);
}

// ---------------------------------------------------------------------------
// Automated cleaning + geo-location parameter preparation layer.
//
// Takes the parsed/normalized cinema footprint rows and produces a cleaned,
// deduplicated dataset plus targeting-ready geo-location parameters that can
// later feed AI Analysis and (in a future phase) cross-platform media
// activation. This pass is frontend-first: it computes and exposes outputs but
// performs no backend persistence or live platform writes. No row is silently
// discarded — rows that cannot be cleaned are kept and flagged.
// ---------------------------------------------------------------------------

const DEFAULT_GEOFENCE_RADIUS = 5;
const DEFAULT_GEOFENCE_RADIUS_UNIT = "mi";
// New Cinema footprints begin with a deterministic 5-mile default. Once a
// canonical policy is persisted, its configurable bounds/default remain the
// authority and coordinate-ready rows inherit that resolved value.
const DEFAULT_TARGETING_RADIUS_MILES = 5;
const MAX_TARGETING_RADIUS_MILES = 500;
// Venues that match no governed chain rule. Sourced from the shared chain map
// so the label cannot drift between the parser, the page and the backend.
const UNKNOWN_CIRCUIT_LABEL = INDEPENDENT_CHAIN_LABEL;

function toTitleCase(value) {
  const normalized = normalizeValue(value);
  if (!normalized) return "";
  return normalized.toLowerCase().replace(/\b[a-z]/g, (char) => char.toUpperCase());
}

// US/Canada-style region codes ("ny", "ca", "ont") are upper-cased; longer
// region names are title-cased.
function normalizeRegionToken(value) {
  const normalized = normalizeValue(value);
  if (!normalized) return "";
  if (/^[a-z]{2,3}$/i.test(normalized)) return normalized.toUpperCase();
  return toTitleCase(normalized);
}

/**
 * Canonical exhibitor chain for one row.
 *
 * Theatrical footprints frequently ship with NO circuit column at all — the
 * Never Stop Chasing source is Theater/Street Address/City/State/ZIP only. This
 * used to resolve every one of its 62 AMC venues to "Independent / Unknown",
 * because the chain was read exclusively from the (absent) column. The governed
 * chain map derives the chain from the full theater name instead, while the
 * theater's own display name is left untouched.
 */
function normalizeCircuitName(value, theaterName = "") {
  const raw = normalizeValue(value);
  if (raw && !/^(unknown|n\/?a|none|null|independent)$/i.test(raw)) {
    const resolved = resolveCinemaChain({ circuit: raw, theaterName });
    // A governed circuit normalizes to its canonical label; an exhibitor this
    // map does not know yet is real evidence and is kept verbatim.
    return resolved.label;
  }
  return resolveCinemaChain({ theaterName }).label;
}

function normalizeTheaterDisplayName(value) {
  return normalizeValue(value).replace(/\s+/g, " ");
}

function normalizePostalCode(value) {
  return normalizeValue(value).toUpperCase().replace(/\s+/g, " ");
}

function isValidLatitude(value) {
  return typeof value === "number" && Number.isFinite(value) && value >= -90 && value <= 90;
}

function isValidLongitude(value) {
  return typeof value === "number" && Number.isFinite(value) && value >= -180 && value <= 180;
}

function buildBreakdown(rows, selector) {
  const counts = new Map();
  rows.forEach((row) => {
    const key = normalizeValue(selector(row)) || "Unknown";
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  return Array.from(counts.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

function buildCleanedCinemaRow(row, index) {
  const latitude = isValidLatitude(row.latitude) ? row.latitude : null;
  const longitude = isValidLongitude(row.longitude) ? row.longitude : null;
  const hasCoordinates = latitude != null && longitude != null;
  const coordinatesMalformed =
    (row.latitude != null && latitude == null) ||
    (row.longitude != null && longitude == null) ||
    Boolean(row.hasPartialCoordinates);

  const theaterName = normalizeTheaterDisplayName(row.theaterName);
  const circuitName = normalizeCircuitName(row.circuitName, theaterName);
  const address = normalizeValue(row.address);
  const city = toTitleCase(row.city);
  const state = normalizeRegionToken(row.state);
  const country = normalizeRegionToken(row.country);
  const postalCode = normalizePostalCode(row.postalCode);
  const market = normalizeValue(row.market);

  const hasAddress = Boolean(address) || (Boolean(city) && Boolean(state)) || Boolean(postalCode);
  const hasMarket = Boolean(market);

  const normalizationFlags = [];
  if (coordinatesMalformed) normalizationFlags.push("coordinates_malformed");
  if (circuitName === UNKNOWN_CIRCUIT_LABEL) normalizationFlags.push("circuit_unknown");
  if (!theaterName) normalizationFlags.push("theater_name_missing");

  let readinessStatus;
  if (hasCoordinates) readinessStatus = "geofence_ready";
  else if (hasAddress) readinessStatus = "geocoding_required";
  else if (hasMarket) readinessStatus = "market_targeting_ready";
  else readinessStatus = "cleanup_required";

  return {
    index,
    theaterName,
    circuitName,
    address,
    city,
    state,
    postalCode,
    country,
    latitude,
    longitude,
    market,
    screenCount: row.screenCount ?? null,
    audienceMetric: row.audienceMetric ?? null,
    hasCoordinates,
    hasAddress,
    hasMarket,
    coordinatesMalformed,
    addressComponents: { address, city, state, postalCode, country },
    readinessStatus,
    likelyDuplicate: false,
    mergedDuplicateCount: 0,
    normalizationFlags,
  };
}

function buildExactSignature(cleaned) {
  return [
    cleaned.theaterName.toLowerCase(),
    cleaned.circuitName.toLowerCase(),
    cleaned.address.toLowerCase(),
    cleaned.city.toLowerCase(),
    cleaned.state.toLowerCase(),
    cleaned.postalCode.toLowerCase(),
    cleaned.market.toLowerCase(),
    cleaned.latitude ?? "",
    cleaned.longitude ?? "",
  ].join("|");
}

// Looser key for "likely duplicate" detection (same venue, not byte-identical).
function buildSimilarityKey(cleaned) {
  if (cleaned.hasCoordinates) {
    return `geo:${cleaned.latitude.toFixed(4)},${cleaned.longitude.toFixed(4)}`;
  }
  const name = cleaned.theaterName.toLowerCase();
  if (!name && !cleaned.city) return "";
  return `name:${name}|${cleaned.city.toLowerCase()}|${cleaned.state.toLowerCase()}`;
}

function resolveDatasetReadiness(summary) {
  if (summary.totalRows === 0) return "Missing Required Location Fields";
  const actionable =
    summary.geofenceReadyCount +
    summary.geocodingRequiredCount +
    summary.marketTargetingReadyCount;
  if (actionable === 0) {
    return summary.duplicateReviewCount > 0 ? "Cleanup Required" : "Missing Required Location Fields";
  }
  if (
    summary.geofenceReadyCount > 0 &&
    summary.geocodingRequiredCount === 0 &&
    summary.cleanupRequiredCount === 0
  ) {
    return "Location Intelligence Ready";
  }
  if (summary.geocodingRequiredCount > 0) return "Geocoding Required";
  if (summary.marketTargetingReadyCount > 0 && summary.geofenceReadyCount === 0) {
    return "Market Targeting Ready";
  }
  if (summary.cleanupRequiredCount > 0) return "Cleanup Required";
  return "Location Intelligence Ready";
}

export function buildCinemaGeoLocationAutomation(normalizedRows) {
  const rows = Array.isArray(normalizedRows) ? normalizedRows : [];
  const cleanedRows = [];
  const exactSignatures = new Map();
  const similarityKeys = new Map();
  let duplicatesCollapsed = 0;

  rows.forEach((row, index) => {
    const cleaned = buildCleanedCinemaRow(row, index);
    const signature = buildExactSignature(cleaned);

    if (exactSignatures.has(signature)) {
      // Safe collapse: a byte-identical row was already kept. Record the merge
      // on the canonical row instead of dropping data silently.
      duplicatesCollapsed += 1;
      const canonical = cleanedRows[exactSignatures.get(signature)];
      canonical.mergedDuplicateCount += 1;
      return;
    }
    exactSignatures.set(signature, cleanedRows.length);

    const similarityKey = buildSimilarityKey(cleaned);
    if (similarityKey && similarityKeys.has(similarityKey)) {
      // Same venue but not identical — unsafe to merge, so flag for review.
      cleaned.likelyDuplicate = true;
      cleaned.readinessStatus = "duplicate_review";
      cleaned.normalizationFlags.push("duplicate_review");
    } else if (similarityKey) {
      similarityKeys.set(similarityKey, cleanedRows.length);
    }

    cleanedRows.push(cleaned);
  });

  const geofences = [];
  const geocodingQueue = [];
  const marketTargets = [];
  const cleanupQueue = [];

  cleanedRows.forEach((cleaned) => {
    if (cleaned.readinessStatus === "geofence_ready") {
      geofences.push({
        theaterName: cleaned.theaterName,
        circuit: cleaned.circuitName,
        latitude: cleaned.latitude,
        longitude: cleaned.longitude,
        radius: DEFAULT_GEOFENCE_RADIUS,
        radiusUnit: DEFAULT_GEOFENCE_RADIUS_UNIT,
        city: cleaned.city,
        state: cleaned.state,
        market: cleaned.market,
        readinessStatus: cleaned.readinessStatus,
      });
    } else if (cleaned.readinessStatus === "geocoding_required") {
      geocodingQueue.push({
        theaterName: cleaned.theaterName,
        circuit: cleaned.circuitName,
        address: cleaned.address,
        city: cleaned.city,
        state: cleaned.state,
        postalCode: cleaned.postalCode,
        country: cleaned.country,
        geocodingRequired: true,
        suggestedGeocodingInput: [
          cleaned.address,
          cleaned.city,
          cleaned.state,
          cleaned.postalCode,
          cleaned.country,
        ]
          .filter(Boolean)
          .join(", "),
        readinessStatus: cleaned.readinessStatus,
      });
    } else if (cleaned.readinessStatus === "market_targeting_ready") {
      marketTargets.push({
        theaterName: cleaned.theaterName,
        circuit: cleaned.circuitName,
        market: cleaned.market,
        marketTargetingReady: true,
        readinessStatus: cleaned.readinessStatus,
      });
    } else {
      cleanupQueue.push({
        theaterName: cleaned.theaterName,
        circuit: cleaned.circuitName,
        reason: cleaned.likelyDuplicate ? "duplicate_review" : "cleanup_required",
        normalizationFlags: cleaned.normalizationFlags,
        readinessStatus: cleaned.readinessStatus,
      });
    }
  });

  const summary = {
    totalRows: rows.length,
    cleanedRowCount: cleanedRows.length,
    duplicatesCollapsed,
    validTheaterRows: cleanedRows.filter((r) => r.readinessStatus !== "cleanup_required").length,
    theaterCount: uniqueCount(cleanedRows, (r) => r.theaterName || r.address || r.market),
    circuitCount: uniqueCount(cleanedRows, (r) => r.circuitName),
    marketCount: uniqueCount(cleanedRows, (r) => r.market || r.state || r.city),
    cityCount: uniqueCount(cleanedRows, (r) => r.city),
    stateCount: uniqueCount(cleanedRows, (r) => r.state),
    geofenceReadyCount: geofences.length,
    geocodingRequiredCount: geocodingQueue.length,
    marketTargetingReadyCount: marketTargets.length,
    cleanupRequiredCount: cleanedRows.filter((r) => r.readinessStatus === "cleanup_required").length,
    duplicateReviewCount: cleanedRows.filter((r) => r.readinessStatus === "duplicate_review").length,
  };

  const datasetReadiness = resolveDatasetReadiness(summary);

  const warnings = [];
  if (duplicatesCollapsed > 0) {
    warnings.push(`${duplicatesCollapsed} exact duplicate row(s) collapsed during cleaning.`);
  }
  if (summary.duplicateReviewCount > 0) {
    warnings.push(`${summary.duplicateReviewCount} row(s) flagged as likely duplicates for review.`);
  }
  if (summary.geocodingRequiredCount > 0) {
    warnings.push(`${summary.geocodingRequiredCount} row(s) require geocoding before geo-fence export.`);
  }
  if (summary.marketTargetingReadyCount > 0) {
    warnings.push(`${summary.marketTargetingReadyCount} row(s) are market-targeting ready only.`);
  }
  if (summary.cleanupRequiredCount > 0) {
    warnings.push(`${summary.cleanupRequiredCount} row(s) need cleanup before activation.`);
  }

  const automation = {
    summary,
    cleanedRows,
    geolocationParameters: { geofences, geocodingQueue, marketTargets, cleanupQueue },
    circuitBreakdown: buildBreakdown(cleanedRows, (r) => r.circuitName || UNKNOWN_CIRCUIT_LABEL),
    marketBreakdown: buildBreakdown(cleanedRows, (r) => r.market || r.state || r.city || "Unknown Market"),
    datasetReadiness,
    warnings,
  };

  // Prepared geo-targeting parameters (with the default radius). The UI can
  // recompute this with an adjusted radius via buildCinemaTargetingPrep.
  automation.targetingPrep = buildCinemaTargetingPrep(automation, {
    defaultRadiusMiles: DEFAULT_TARGETING_RADIUS_MILES,
  });

  return automation;
}

export function normalizeTargetingRadiusMiles(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return DEFAULT_TARGETING_RADIUS_MILES;
  return Math.min(Math.round(parsed * 100) / 100, MAX_TARGETING_RADIUS_MILES);
}

/**
 * Build prepared, frontend-safe geo-targeting parameters from a cleaned cinema
 * automation result. Pure: never mutates its input. Coordinate-ready rows get
 * the default radius; every other row is routed into a queue (no row dropped).
 */
export function buildCinemaTargetingPrep(automation, options = {}) {
  const cleanedRows = Array.isArray(automation?.cleanedRows) ? automation.cleanedRows : [];
  const defaultRadiusMiles = normalizeTargetingRadiusMiles(options.defaultRadiusMiles);

  const geofenceTargets = [];
  const cityTargets = [];
  const stateTargets = [];
  const zipTargets = [];
  const marketTargets = [];
  const geocodingQueue = [];
  const cleanupQueue = [];
  const duplicateReview = [];

  const citySeen = new Set();
  const stateSeen = new Set();
  const zipSeen = new Set();
  const marketSeen = new Set();

  cleanedRows.forEach((row) => {
    // Grouped geo dimensions are derived from any row that carries the field,
    // de-duplicated, regardless of the row's overall readiness status.
    if (row.city) {
      const key = `${row.city.toLowerCase()}|${String(row.state || "").toLowerCase()}`;
      if (!citySeen.has(key)) {
        citySeen.add(key);
        cityTargets.push({ city: row.city, state: row.state || null });
      }
    }
    if (row.state) {
      const key = row.state.toLowerCase();
      if (!stateSeen.has(key)) {
        stateSeen.add(key);
        stateTargets.push({ state: row.state });
      }
    }
    if (row.postalCode) {
      const key = row.postalCode.toLowerCase();
      if (!zipSeen.has(key)) {
        zipSeen.add(key);
        zipTargets.push({ zip: row.postalCode, city: row.city || null, state: row.state || null });
      }
    }
    if (row.market) {
      const key = row.market.toLowerCase();
      if (!marketSeen.has(key)) {
        marketSeen.add(key);
        marketTargets.push({ market: row.market });
      }
    }

    // Per-row readiness routing (a single bucket per row; nothing discarded).
    if (row.readinessStatus === "duplicate_review" || row.likelyDuplicate) {
      duplicateReview.push({
        theaterName: row.theaterName,
        circuit: row.circuitName,
        city: row.city,
        state: row.state,
        reason: "duplicate_review",
        readinessStatus: row.readinessStatus,
      });
      return;
    }

    if (row.readinessStatus === "geofence_ready") {
      geofenceTargets.push({
        theaterName: row.theaterName,
        circuit: row.circuitName,
        city: row.city,
        state: row.state,
        zip: row.postalCode,
        market: row.market,
        latitude: row.latitude,
        longitude: row.longitude,
        radius: defaultRadiusMiles,
        radiusUnit: "miles",
        radiusSource: "default",
        readinessStatus: row.readinessStatus,
      });
    } else if (row.readinessStatus === "geocoding_required") {
      geocodingQueue.push({
        theaterName: row.theaterName,
        circuit: row.circuitName,
        address: row.address,
        city: row.city,
        state: row.state,
        postalCode: row.postalCode,
        country: row.country,
        suggestedGeocodingInput: [row.address, row.city, row.state, row.postalCode, row.country]
          .filter(Boolean)
          .join(", "),
        readinessStatus: row.readinessStatus,
      });
    } else if (row.readinessStatus === "cleanup_required") {
      cleanupQueue.push({
        theaterName: row.theaterName,
        circuit: row.circuitName,
        reason: "cleanup_required",
        normalizationFlags: row.normalizationFlags || [],
        readinessStatus: row.readinessStatus,
      });
    }
    // market_targeting_ready rows already contributed to marketTargets above.
  });

  return {
    defaultRadiusMiles,
    geofenceTargets,
    cityTargets,
    stateTargets,
    zipTargets,
    marketTargets,
    geocodingQueue,
    cleanupQueue,
    duplicateReview,
    recommendedNextStep: getRecommendedNextStep(automation).step,
  };
}

// ---------------------------------------------------------------------------
// Chart-ready, decision-support helpers.
//
// Pure transforms over the automation object for the simplified, chart-based
// Location Intelligence UI. They never mutate their inputs and are safe to call
// with a missing/partial automation object.
// ---------------------------------------------------------------------------

const READINESS_CHART_SEGMENTS = [
  { key: "geofence_ready", label: "Geo-Fence Ready", color: "#39d98a", summaryField: "geofenceReadyCount" },
  { key: "geocoding_required", label: "Geocoding Required", color: "#ffaa00", summaryField: "geocodingRequiredCount" },
  { key: "market_targeting_ready", label: "Market Targeting Ready", color: "#889ce7", summaryField: "marketTargetingReadyCount" },
  { key: "cleanup_required", label: "Cleanup Required", color: "#ff7a8a", summaryField: "cleanupRequiredCount" },
  { key: "duplicate_review", label: "Duplicate Review", color: "#c24de7", summaryField: "duplicateReviewCount" },
];

function toChartItems(breakdown, limit) {
  const list = Array.isArray(breakdown) ? breakdown : [];
  const total = list.reduce((sum, item) => sum + (Number(item?.count) || 0), 0);
  const safeLimit = Number.isFinite(limit) && limit > 0 ? limit : 8;
  const top = list.slice(0, safeLimit);
  const remainder = list.slice(safeLimit);

  const items = top.map((item) => ({
    label: item.label,
    value: Number(item.count) || 0,
    percent: total > 0 ? Math.round(((Number(item.count) || 0) / total) * 100) : 0,
  }));

  const otherValue = remainder.reduce((sum, item) => sum + (Number(item?.count) || 0), 0);
  if (otherValue > 0) {
    items.push({
      label: "Other",
      value: otherValue,
      percent: total > 0 ? Math.round((otherValue / total) * 100) : 0,
      isOther: true,
    });
  }

  return { items, total, otherCount: remainder.length };
}

export function getTopCircuitChartData(automation, limit = 8) {
  return toChartItems(automation?.circuitBreakdown, limit);
}

export function getTopMarketChartData(automation, limit = 8) {
  return toChartItems(automation?.marketBreakdown, limit);
}

export function getReadinessChartData(automation) {
  const summary = automation?.summary || {};
  const segments = READINESS_CHART_SEGMENTS.map((seg) => ({
    key: seg.key,
    label: seg.label,
    color: seg.color,
    value: Number(summary[seg.summaryField]) || 0,
  }));
  const total = segments.reduce((sum, seg) => sum + seg.value, 0);
  return {
    segments,
    total,
    readyPercent: total > 0 ? Math.round(((Number(summary.geofenceReadyCount) || 0) / total) * 100) : 0,
  };
}

export function getLocationReadinessPercent(automation) {
  return getReadinessChartData(automation).readyPercent;
}

export function getRecommendedNextStep(automation) {
  const s = automation?.summary || {};
  const total = Number(s.totalRows) || 0;
  if (!total) {
    return { step: "Upload a CinemaCloudWorks CSV", detail: "No cinema footprint rows detected yet." };
  }

  const cleanup = Number(s.cleanupRequiredCount) || 0;
  const geocoding = Number(s.geocodingRequiredCount) || 0;
  const geofence = Number(s.geofenceReadyCount) || 0;
  const market = Number(s.marketTargetingReadyCount) || 0;
  const duplicates = Number(s.duplicateReviewCount) || 0;

  if (cleanup > 0 && cleanup >= geocoding && cleanup >= geofence) {
    return { step: "Clean missing location fields", detail: `${cleanup} row(s) are missing required location identifiers.` };
  }
  if (geocoding > 0) {
    return { step: "Run geocoding before export", detail: `${geocoding} address row(s) need coordinates for geo-fence preparation.` };
  }
  if (duplicates > 0 && geofence === 0) {
    return { step: "Review duplicate theater records", detail: `${duplicates} likely duplicate row(s) need review.` };
  }
  if (geofence > 0) {
    return { step: "Ready for geo-targeting preparation", detail: `${geofence} location(s) are geo-fence ready.` };
  }
  if (market > 0) {
    return { step: "Market-level targeting only", detail: `${market} row(s) support market or DMA level targeting.` };
  }
  return { step: "Clean missing location fields", detail: "Add coordinates, address, or market fields to proceed." };
}

// One-line, executive-readable summary of what Cphere determined from the upload.
export function getCinemaFootprintHeadline(automation) {
  const s = automation?.summary || {};
  const ready = Number(s.geofenceReadyCount) || 0;
  const needsWork = (Number(s.geocodingRequiredCount) || 0) + (Number(s.cleanupRequiredCount) || 0);
  return (
    `Cphere identified ${Number(s.theaterCount) || 0} theaters across ${Number(s.circuitCount) || 0} circuits ` +
    `and ${Number(s.marketCount) || 0} markets. ${ready} location(s) are ready for geo-fence preparation; ` +
    `${needsWork} require cleanup or geocoding.`
  );
}

export function parseCinemaCloudWorksCsvPreview(text, options = {}) {
  const fileName = normalizeValue(options.fileName) || "CinemaCloudWorks.csv";
  const base = {
    ok: false,
    fileName,
    statusMessage: "",
    readinessLabel: "Missing Required Location Fields",
    headers: [],
    rows: [],
    previewRows: [],
    metrics: {
      totalRows: 0,
      totalColumns: 0,
      theaterCount: 0,
      circuitCount: 0,
      marketCount: 0,
      coordinateReadyCount: 0,
      geocodingRequiredCount: 0,
      marketOnlyCount: 0,
      missingLocationCount: 0,
      cleanupNeededCount: 0,
    },
    marketDistribution: [],
    warnings: [],
    analysisContext: null,
  };

  if (!normalizeValue(text)) {
    return {
      ...base,
      statusMessage: "Empty CSV. Upload a CinemaCloudWorks CSV with theater or location rows.",
      warnings: ["Empty CSV. No cinema footprint rows were detected."],
    };
  }

  let csvRows;
  try {
    csvRows = parseCsvRows(text);
  } catch (err) {
    return {
      ...base,
      statusMessage: err.message,
      warnings: [err.message],
    };
  }

  if (csvRows.length < 2) {
    return {
      ...base,
      statusMessage: "Missing data rows. The CSV needs headers and at least one cinema location row.",
      headers: csvRows[0] || [],
      metrics: { ...base.metrics, totalColumns: csvRows[0]?.length || 0 },
      warnings: ["Missing data rows. The CSV needs headers and at least one cinema location row."],
    };
  }

  const headerIndex = locateCinemaTableHeaderIndex(csvRows);
  const headers = csvRows[headerIndex].map((header) => normalizeValue(header));
  const fieldMap = buildFieldMap(headers);
  const dataRows = sliceCinemaTableRows(csvRows, headerIndex, headers, fieldMap);
  const normalizedRows = dataRows.map((row) => normalizeCinemaRow(row, headers, fieldMap));
  const coordinateReadyCount = normalizedRows.filter((row) => row.hasCoordinates).length;
  const geocodingRequiredCount = normalizedRows.filter((row) => !row.hasCoordinates && row.hasAddress).length;
  const marketOnlyCount = normalizedRows.filter(
    (row) => !row.hasCoordinates && !row.hasAddress && row.hasMarket
  ).length;
  const missingLocationCount = normalizedRows.filter(
    (row) => !row.hasCoordinates && !row.hasAddress && !row.hasMarket
  ).length;
  const hasLocationFields =
    fieldMap.latitude >= 0 ||
    fieldMap.longitude >= 0 ||
    fieldMap.address >= 0 ||
    fieldMap.city >= 0 ||
    fieldMap.state >= 0 ||
    fieldMap.postalCode >= 0 ||
    fieldMap.market >= 0;
  const warnings = [];

  if (headerIndex > 0) {
    warnings.push(
      `Ignored ${headerIndex} introductory row(s) before the theater table header; summary rows after the table were also skipped.`
    );
  }
  if (!hasLocationFields) {
    warnings.push("Missing required location fields. Include coordinates, address fields, or market/DMA data.");
  }
  if (normalizedRows.some((row) => row.hasPartialCoordinates)) {
    warnings.push("Some rows include only latitude or longitude. Both are required for geo-fence preparation.");
  }
  if (geocodingRequiredCount > 0) {
    warnings.push("Address-only rows detected: geocoding required before geo-fence export.");
  }
  if (marketOnlyCount > 0) {
    warnings.push("Market-only rows detected: usable for market-level targeting, not precise geo-fencing.");
  }
  if (missingLocationCount > 0) {
    warnings.push("Some rows are missing coordinates, address fields, and market/DMA data.");
  }

  const readinessLabel =
    !hasLocationFields || missingLocationCount === normalizedRows.length
      ? "Missing Required Location Fields"
      : geocodingRequiredCount > 0 || missingLocationCount > 0
      ? "Geocoding Required"
      : coordinateReadyCount > 0
      ? "Location Intelligence Ready"
      : "Market Targeting Ready";
  const automation = buildCinemaGeoLocationAutomation(normalizedRows);
  const metrics = {
    totalRows: normalizedRows.length,
    totalColumns: headers.length,
    theaterCount: uniqueCount(normalizedRows, (row) => row.theaterName || row.address || row.market),
    // Counted on the DERIVED canonical chain, not the raw circuit column: a
    // source with no exhibitor column previously reported 0 circuits for a
    // 75-theater, AMC-dominated footprint.
    circuitCount: automation.circuitBreakdown.length,
    marketCount: uniqueCount(normalizedRows, (row) => row.market || row.state || row.city),
    coordinateReadyCount,
    geocodingRequiredCount,
    marketOnlyCount,
    missingLocationCount,
    cleanupNeededCount: geocodingRequiredCount + missingLocationCount + normalizedRows.filter((row) => row.hasPartialCoordinates).length,
  };
  const marketDistribution = buildMarketDistribution(normalizedRows);

  return {
    ...base,
    ok: true,
    statusMessage: "Cinema footprint loaded",
    readinessLabel,
    headers,
    rows: normalizedRows,
    previewRows: normalizedRows.slice(0, MAX_PREVIEW_ROWS),
    metrics,
    marketDistribution,
    // Automated cleaning + geo-location targeting parameters derived from the
    // uploaded footprint (cleaned rows, geofences, geocoding queue, etc.).
    automation,
    warnings,
    analysisContext: {
      theaterCount: metrics.theaterCount,
      circuitCount: metrics.circuitCount,
      marketDistribution,
      coordinateReadiness: {
        coordinateReadyCount,
        geocodingRequiredCount,
        marketOnlyCount,
        missingLocationCount,
      },
      addressGeocodingGaps: geocodingRequiredCount + missingLocationCount,
      highDensityCinemaAreas: marketDistribution.slice(0, 3),
      locationIntelligenceReadiness: readinessLabel,
      // Targeting-ready outputs exposed for AI Analysis / future activation.
      datasetReadiness: automation.datasetReadiness,
      geoLocationParameters: {
        geofenceReadyCount: automation.summary.geofenceReadyCount,
        geocodingRequiredCount: automation.summary.geocodingRequiredCount,
        marketTargetingReadyCount: automation.summary.marketTargetingReadyCount,
        cleanupRequiredCount: automation.summary.cleanupRequiredCount,
        duplicateReviewCount: automation.summary.duplicateReviewCount,
      },
      circuitBreakdown: automation.circuitBreakdown.slice(0, 5),
      recommendedNextStep: getRecommendedNextStep(automation).step,
      // Prepared geo-targeting context for AI Analysis.
      targetingPrep: {
        defaultRadiusMiles: automation.targetingPrep.defaultRadiusMiles,
        geofenceTargetCount: automation.targetingPrep.geofenceTargets.length,
        cityTargetCount: automation.targetingPrep.cityTargets.length,
        stateTargetCount: automation.targetingPrep.stateTargets.length,
        zipTargetCount: automation.targetingPrep.zipTargets.length,
        marketTargetCount: automation.targetingPrep.marketTargets.length,
        geocodingQueueCount: automation.targetingPrep.geocodingQueue.length,
        cleanupQueueCount: automation.targetingPrep.cleanupQueue.length,
        recommendedNextStep: automation.targetingPrep.recommendedNextStep,
      },
    },
  };
}

export function isCinemaCsvFile(file) {
  const name = String(file?.name || "").toLowerCase();
  const type = String(file?.type || "").toLowerCase();
  return name.endsWith(".csv") || type === "text/csv" || type === "application/vnd.ms-excel";
}
